import AxiosService from '@/services/api/AxiosService';
import User, { DepartmentUser } from '@/entities/User';
import { AdminUser } from '@/entities/models/AdminUser';
import { UserSettings } from '@/entities/UserSettings';
import ApiLinks from '@/entities/ApiLinks';
import { ApiQueries } from '@/entities/ApiQueries';
import ApiStatus from '@/entities/enums/apiStatuses';
import { vxm } from '@/store/store.vuex';
import { AdminMember } from '@/entities/models/AdminMember';
import ApiResponse from '@/entities/ApiResponse';
import { ReceivedInvitation } from '@/entities/models/ReceivedInvitation';
import { CustomerPermissions } from '../permissions/CustomerPermissions';
import { AdminPermissions } from '../permissions/AdminPermissions';
import LoggedUserManager from '../LoggedUserManager';
const userCan = LoggedUserManager.checkPermissions;
var vxDepartmentStore = vxm.departmentStore;
export default class UserRepository {
    constructor() { }
    async getUsers(departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.USERS_LIST)) {
            return [];
        }
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Department.Keys.Users}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return [];
        else
            return User.fromApi(response.data.data);
    }
    async getDepartmentUsers(departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.USERS_LIST)) {
            return [];
        }
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Department.Keys.Users}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return [];
        else
            return DepartmentUser.fromApi(response.data.data);
    }
    async getCurrentUser() {
        let url = `${ApiLinks.Profile.Me}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return new User();
        else
            return User.fromApi(response.data.data);
    }
    async updateCurrentUser(userData) {
        let url = `${ApiLinks.Profile.Me}`;
        let response = await AxiosService.putDataToServer(url, userData);
        if (response?.status != ApiStatus.SUCCESS)
            return new User();
        else
            return User.fromApi(response.data.data);
    }
    async deletePhoneNumber() {
        let url = `${ApiLinks.Profile.Phone}`;
        let response = await AxiosService.deleteDataFromServer(url, null);
        return response?.status === ApiStatus.NO_CONTENT;
    }
    async createPhoneNumber(phoneNumber) {
        let url = `${ApiLinks.Profile.Phone}`;
        let data = new FormData();
        data.set('phoneNumber', phoneNumber);
        let response = await AxiosService.postDataToServer(url, data, false);
        if (response?.status == ApiStatus.SUCCESS)
            return true;
        else
            return response.data;
    }
    async sendVerificationToCodePhoneNumber(phoneNumber) {
        let url = `${ApiLinks.Profile.SendCode}`;
        let data = new FormData();
        data.set('phoneNumber', phoneNumber);
        let response = await AxiosService.postDataToServer(url, data);
        if (response?.status != ApiStatus.SUCCESS)
            return response?.data;
        else
            return true;
    }
    async verifyPhoneNumber(phoneNumber, code) {
        let url = `${ApiLinks.Profile.VerifyNumber}`;
        let data = new FormData();
        data.set('phoneNumber', phoneNumber);
        data.set('verificationCode', code);
        let response = await AxiosService.postDataToServer(url, data, false);
        if (response?.status != ApiStatus.SUCCESS)
            return response?.data;
        else
            return true;
    }
    async enableUser(userId, departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.USERS_ENABLE)) {
            throw 'Forbidden action enableUser';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Department.Keys.Users}/${userId}${ApiLinks.Department.Keys.Enable}`;
        let response = await AxiosService.postDataToServer(url, null);
        if (response?.status != ApiStatus.SUCCESS)
            return false;
        else
            return DepartmentUser.fromApi(response.data.data);
    }
    async disableUser(userId, departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.USERS_DISABLE)) {
            throw 'Forbidden action disableUser';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Department.Keys.Users}/${userId}${ApiLinks.Department.Keys.Disable}`;
        let response = await AxiosService.postDataToServer(url, null);
        if (response?.status === ApiStatus.NOT_ALLOWED)
            return response.data.code;
        else if (response?.status != ApiStatus.SUCCESS)
            return false;
        else
            return DepartmentUser.fromApi(response.data.data);
    }
    async getUser(userId, departmentId = this.getSelectedDepartmentId(), processError) {
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Users.Keys.Users}/${userId}`;
        let response = await AxiosService.getDataFromServer(url, processError);
        if (response?.status != ApiStatus.SUCCESS)
            return new DepartmentUser();
        else
            return DepartmentUser.fromApi(response.data.data);
    }
    async removeUserFromDepartment(userId, departmentId = this.getSelectedDepartmentId()) {
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Users.Keys.Users}/${userId}`;
        let response = await AxiosService.deleteDataFromServer(url, null);
        return response?.status === ApiStatus.SUCCESS;
    }
    async changeUserRole(userId, role, departmentId = this.getSelectedDepartmentId()) {
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Users.Keys.Users}/${userId}${ApiLinks.Users.Keys.Role}`;
        let data = {
            RoleKey: role
        };
        let response = await AxiosService.postDataToServer(url, data);
        if (response?.status != ApiStatus.SUCCESS)
            return new DepartmentUser();
        else
            return DepartmentUser.fromApi(response.data.data);
    }
    async changeDepartmentOwner(userId, departmentId = this.getSelectedDepartmentId()) {
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Users.Keys.Owner}`;
        let data = {
            UserId: userId
        };
        let response = await AxiosService.postDataToServer(url, data);
        return response?.status === ApiStatus.SUCCESS;
    }
    async getUserRoles(departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.COMPANY_MANAGE_OWN)) {
            throw 'Forbidden action getUserRoles';
        }
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Department.Keys.Roles}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return [];
        else
            return response.data.data;
    }
    async getUserRole(userId, departmentId = this.getSelectedDepartmentId()) {
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Department.Keys.Roles}/${userId}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return response.data.data;
    }
    async getUsersAdmin(departmentId = null, pagination = null, sort = null, filter = []) {
        let userAdminResponse = new ApiResponse();
        if (!userCan(AdminPermissions.ADMIN_USERS_LIST)) {
            return userAdminResponse;
        }
        let url = ApiLinks.Users.AdminHome;
        let queries = new ApiQueries();
        if (departmentId) {
            queries.departmentId = departmentId;
        }
        queries.addPagination(pagination);
        if (sort)
            queries.addSorts(sort);
        if (filter.length > 0) {
            queries.addFilters(filter);
        }
        if (queries.hasQueries())
            url += `?${queries.toQueryString()}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return userAdminResponse;
        else {
            userAdminResponse.setPagination(response.data);
            userAdminResponse.setData(AdminUser.fromApi(response.data.data));
            return userAdminResponse;
        }
    }
    async adminEnableUser(userId, data) {
        if (!userCan(AdminPermissions.ADMIN_USERS_ENABLE)) {
            throw 'Forbidden action adminEnableUser';
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.Enable}`;
        let response = await AxiosService.postDataToServer(url, data);
        if (response?.status != ApiStatus.SUCCESS)
            return new User();
        else
            return User.fromApi(response.data.data);
    }
    async adminDisableUser(userId, data) {
        if (!userCan(AdminPermissions.ADMIN_USERS_DISABLE)) {
            throw 'Forbidden action adminDisableUser';
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.Disable}`;
        let response = await AxiosService.postDataToServer(url, data);
        if (response?.status != ApiStatus.SUCCESS)
            return new User();
        else
            return User.fromApi(response.data.data);
    }
    async adminSaveUserChanges(userId, user) {
        if (!userCan(AdminPermissions.ADMIN_USERS_UPDATE)) {
            throw 'Forbidden action adminDisableUser';
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}`;
        let response = await AxiosService.putDataToServer(url, user);
        if (response?.status != ApiStatus.SUCCESS)
            return new User();
        else
            return User.fromApi(response.data.data);
    }
    async getAdminUser(userId) {
        if (!userCan(AdminPermissions.ADMIN_USERS_READ)) {
            return new AdminUser();
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return new AdminUser();
        else
            return AdminUser.fromApi(response.data.data);
    }
    async adminDeleteUser(userId) {
        let url = `${ApiLinks.Users.AdminHome}/${userId}`;
        let response = await AxiosService.deleteDataFromServer(url, null);
        return response?.status === ApiStatus.NO_CONTENT;
    }
    /**
     * Gets admin user settings and department settings for all departments. User settings are scoped under profile key
     * @param userId
     * @returns admin user settings
     */
    async getAdminUserSettings(userId) {
        if (!userCan(AdminPermissions.ADMIN_USERS_SETTINGS_READ)) {
            return new UserSettings();
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.Settings}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return new UserSettings();
        else
            return response.data.data;
    }
    // /**
    //  * Gets admin user settings and department settings for all departments. User settings are scoped under profile key
    //  * @param userId
    //  * @returns admin user settings
    //  */
    // async getDepartmentUserSettings(userId: String, userId: String): Promise<any> {
    //   if (!userCan(AdminPermissions.ADMIN_USERS_SETTINGS_READ)) {
    //     return new UserSettings();
    //   }
    //   let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.Settings}`;
    //   let response = await AxiosService.getDataFromServer<AxiosResponse<IResponsePayload<any>>>(url );
    //   if (response?.status != ApiStatus.SUCCESS) return new UserSettings();
    //   else return response.data.data;
    // }
    async adminDeletePhoneNumber(userId) {
        if (!userCan(AdminPermissions.ADMIN_USERS_PHONE_DELETE)) {
            throw 'Forbidden action adminDeletePhoneNumber';
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.PhoneNumber}`;
        let response = await AxiosService.deleteDataFromServer(url, null);
        return response?.status === ApiStatus.NO_CONTENT;
    }
    async adminAddPhoneNumber(userId, phoneNumber) {
        if (!userCan(AdminPermissions.ADMIN_USERS_PHONE_EDIT)) {
            throw 'Forbidden action adminAddPhoneNumber';
        }
        let data = new FormData();
        data.set('phoneNumber', phoneNumber);
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.PhoneNumber}`;
        let response = await AxiosService.postDataToServer(url, data);
        if (response?.status != ApiStatus.SUCCESS)
            return response?.data;
        else
            return true;
    }
    async adminSendVerificationToCodePhoneNumber(userId) {
        if (!userCan(AdminPermissions.ADMIN_USERS_PHONE_EDIT)) {
            throw 'Forbidden action adminSendVerificationToCodePhoneNumber';
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.SendCode}`;
        let response = await AxiosService.postDataToServer(url, null, false);
        if (response?.status != ApiStatus.SUCCESS)
            return response?.data;
        else
            return true;
    }
    async adminVerifyPhoneNumber(code, userId) {
        if (!userCan(AdminPermissions.ADMIN_USERS_PHONE_EDIT)) {
            throw 'Forbidden action adminVerifyPhoneNumber';
        }
        let data = new FormData();
        data.set('verificationCode', code);
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.VerifyNumber}`;
        let response = await AxiosService.postDataToServer(url, data, false);
        if (response?.status != ApiStatus.SUCCESS)
            return response?.data;
        else
            return true;
    }
    async assignAdminUserRole(departmentId, userId, userRole) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_USERS_ADD)) {
            throw 'Forbidden action assignAdminUserRole';
        }
        let data = {
            UserId: userId,
            RoleKey: userRole
        };
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.Members}`;
        let response = await AxiosService.postDataToServer(url, data, false);
        if (response?.status == ApiStatus.SUCCESS)
            return true;
        else
            return response.data;
    }
    async adminCreateUser(userDto) {
        if (!userCan(AdminPermissions.ADMIN_USERS_CREATE)) {
            throw 'Forbidden action adminCreateUser';
        }
        let data = {
            Email: userDto.Email,
            Username: userDto.Username,
            Password: userDto.Password,
            FirstName: userDto.FirstName,
            LastName: userDto.LastName,
            PhoneNumber: userDto.PhoneNumber,
            Role: userDto.Role,
            CustomerData: userDto.CustomerData
        };
        let url = `${ApiLinks.Users.AdminHome}`;
        let response = await AxiosService.postDataToServer(url, data);
        if (response?.status === ApiStatus.CREATED)
            return true;
        else
            return (response || {}).data;
    }
    async adminPreregisterUser(preregisterUserDto) {
        if (!userCan(AdminPermissions.ADMIN_USERS_REGISTER)) {
            throw 'Forbidden action adminCreateUser';
        }
        let data = {
            Email: preregisterUserDto.Email,
            Username: preregisterUserDto.Username,
            FirstName: preregisterUserDto.FirstName,
            LastName: preregisterUserDto.LastName,
            SendVerification: preregisterUserDto.SendVerification
        };
        let url = `${ApiLinks.Users.AdminHome}${ApiLinks.Users.Keys.PreRegister}`;
        let response = await AxiosService.postDataToServer(url, data);
        return response?.status === ApiStatus.CREATED;
    }
    async adminRequestVerification(userId) {
        if (!userCan(AdminPermissions.ADMIN_USERS_CREATE)) {
            throw 'Forbidden action adminCreateUser';
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.RequestVerification}`;
        let response = await AxiosService.postDataToServer(url, null);
        return response?.status === ApiStatus.SUCCESS;
    }
    async adminUnassignUser(departmentId, userId) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_USERS_REMOVE)) {
            throw 'Forbidden action adminUnassignUser';
        }
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.Members}/${userId}`;
        let response = await AxiosService.deleteDataFromServer(url, null);
        return response?.status === ApiStatus.SUCCESS;
    }
    async getCompanyUsers(departmentId = null, pagination = null, sort = null, filter = []) {
        let apiResponse = new ApiResponse();
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_USERS_READ)) {
            return apiResponse;
        }
        let queries = new ApiQueries();
        queries.addPagination(pagination);
        if (sort)
            queries.addSorts(sort);
        if (filter.length > 0) {
            queries.addFilters(filter);
        }
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.Members}`;
        if (departmentId) {
            queries.departmentId = departmentId;
        }
        if (queries.hasQueries())
            url += `?${queries.toQueryString()}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return apiResponse;
        else {
            apiResponse.setPagination(response.data);
            apiResponse.setData(AdminMember.fromApi(response.data.data));
            return apiResponse;
        }
    }
    async adminGetSystemRoles() {
        if (!userCan(AdminPermissions.ADMIN_USERS_READ)) {
            throw 'Forbidden action adminGetSystemRoles';
        }
        let url = `${ApiLinks.Users.AdminHome}${ApiLinks.Department.Keys.Roles}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return [];
        else
            return response.data.data;
    }
    async adminUpdateSystemUserRole(userId, role) {
        if (!userCan(AdminPermissions.ADMIN_USERS_CHANGE_ROLE)) {
            throw 'Forbidden action adminUpdateSystemUserRole';
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Department.Keys.Role}`;
        let data = {
            RoleKey: role
        };
        let response = await AxiosService.putDataToServer(url, data);
        if (response?.status != ApiStatus.SUCCESS)
            return new AdminUser();
        else
            return AdminUser.fromApi(response.data.data);
    }
    async adminGetSystemRole(roleKey) {
        if (!userCan(AdminPermissions.ADMIN_USERS_READ)) {
            throw 'Forbidden action adminGetSystemRole';
        }
        let url = `${ApiLinks.Users.AdminHome}/${ApiLinks.Department.Keys.Roles}/${roleKey}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return [];
        else
            return response.data.data;
    }
    async changeCompanyUserRole(userId, role, departmentId) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_USERS_CHANGEROLE)) {
            throw 'Forbidden action changeCompanyUserRole';
        }
        let data = {
            RoleKey: role
        };
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.Members}/${userId}${ApiLinks.Users.Keys.Role}`;
        let response = await AxiosService.postDataToServer(url, data, false);
        if (response?.status === ApiStatus.FORBIDDEN)
            return response.data.code;
        else if (response?.status != ApiStatus.SUCCESS)
            return false;
        else
            return User.fromApi(response.data.data);
    }
    async adminDisableDepartmentUser(departmentId, userId) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_USERS_DISABLE)) {
            throw 'Forbidden action adminDisableDepartmentUser';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.Members}/${userId}${ApiLinks.Department.Keys.Disable}`;
        let response = await AxiosService.postDataToServer(url, null, false);
        return response?.status === ApiStatus.SUCCESS;
    }
    async adminEnableDepartmentUser(departmentId, userId) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_USERS_ENABLE)) {
            throw 'Forbidden action adminEnableDepartmentUser';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.Members}/${userId}${ApiLinks.Department.Keys.Enable}`;
        let response = await AxiosService.postDataToServer(url, null, false);
        return response?.status === ApiStatus.SUCCESS;
    }
    async getInvitationsList() {
        let url = `${ApiLinks.Department.Home}${ApiLinks.Department.Keys.Invitations}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return [];
        else
            return ReceivedInvitation.fromApi(response.data.data);
    }
    async acceptInvitation(invitationId) {
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.Home}${ApiLinks.Department.Keys.Invitations}/${invitationId}${ApiLinks.Department.Keys.Accept}`;
        let response = await AxiosService.postDataToServer(url, null);
        return response?.status === ApiStatus.SUCCESS;
    }
    async declineInvitation(invitationId) {
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.Home}${ApiLinks.Department.Keys.Invitations}/${invitationId}${ApiLinks.Department.Keys.Decline}`;
        let response = await AxiosService.postDataToServer(url, null);
        return response?.status === ApiStatus.SUCCESS;
    }
    async setAsContactPerson(userId, departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.USERS_MANAGE_CONTACT)) {
            throw 'Forbidden action setAsContactPerson';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Users.Keys.Users}/${userId}${ApiLinks.Users.Keys.MarkContactPerson}`;
        let response = await AxiosService.postDataToServer(url, null);
        return response?.status === ApiStatus.SUCCESS;
    }
    async unsetAsContactPerson(userId, departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.USERS_MANAGE_CONTACT)) {
            throw 'Forbidden action unsetAsContactPerson';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Users.Keys.Users}/${userId}${ApiLinks.Users.Keys.UnmarkContactPerson}`;
        let response = await AxiosService.postDataToServer(url, null);
        return response?.status === ApiStatus.SUCCESS;
    }
    async adminGetContactInfo(userId) {
        if (!userCan(AdminPermissions.ADMIN_USERS_READ)) {
            throw 'Forbidden action adminGetContactInfo';
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.ContactInfo}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status === ApiStatus.SUCCESS)
            return response?.data?.data;
    }
    async adminUpdateContactInfo(updateDto, userId) {
        if (!userCan(AdminPermissions.ADMIN_USERS_UPDATE)) {
            throw 'Forbidden action adminUpdateContactInfo';
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.ContactInfo}`;
        let response = await AxiosService.putDataToServer(url, updateDto);
        if (response?.status !== ApiStatus.SUCCESS)
            return null;
        else
            return response.data.data;
    }
    async adminGetContactPhoto(userId) {
        if (!userCan(AdminPermissions.ADMIN_USERS_READ)) {
            throw 'Forbidden action adminGetContactPhoto';
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.ContactInfo}${ApiLinks.Users.Keys.Photo}`;
        let response = await AxiosService.downloadFile_GET(url);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return response;
    }
    async adminUpdateContactPhoto(image, userId) {
        if (!userCan(AdminPermissions.ADMIN_USERS_UPDATE)) {
            throw 'Forbidden action adminUpdateContactPhoto';
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.ContactInfo}${ApiLinks.Users.Keys.Photo}`;
        let data = new FormData();
        data.append('image', image);
        let response = await AxiosService.postDataToServer(url, data);
        if (response?.status !== ApiStatus.CREATED)
            return null;
        else
            return response.data.data;
    }
    async adminDeleteContactPhoto(userId) {
        if (!userCan(AdminPermissions.ADMIN_USERS_UPDATE)) {
            throw 'Forbidden action adminDeleteContactPhoto';
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.ContactInfo}${ApiLinks.Users.Keys.Photo}`;
        let response = await AxiosService.deleteDataFromServer(url, null);
        return response?.status === ApiStatus.NO_CONTENT;
    }
    getSelectedDepartmentId() {
        return vxDepartmentStore.selectedDepartment.id;
    }
}
